// A customer's invoice page (#512): the link in a payment reminder opens it at `/invoice#<token>`, with no sign-in. It
// names the invoice and offers its PDF, and nothing else of the business. The token rides in the fragment, which the
// browser never sends to a server. An expired or unknown link says so in plain words.
import { useEffect, useState } from "react";
import { useWords } from "../i18n/words.js";
import type { InvoicePage, QuotationLinkApi } from "./api.js";

export const INVOICE_PATH = "/invoice";

const tokenInLink = (): string => decodeURIComponent(window.location.hash.replace(/^#/, "")).trim();

export function InvoiceLink({ api }: { api: Pick<QuotationLinkApi, "openInvoice" | "invoicePdf"> }) {
  const w = useWords();
  const [token] = useState(tokenInLink);
  const [page, setPage] = useState<InvoicePage | null | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (token === "") {
      setPage(null);
      return;
    }
    api.openInvoice(token).then(setPage, () => setProblem(w.quotationUnreachable));
  }, [api, token]);

  const download = async () => {
    try {
      const pdf = await api.invoicePdf(token);
      const url = URL.createObjectURL(pdf);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${page?.number.replace(/[^\w.-]+/g, "-") ?? "invoice"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setProblem(w.quotationUnreachable);
    }
  };

  return (
    <main className="sign-in" id="main">
      {page === undefined && !problem ? <p role="status">{w.invoiceOpening}</p> : null}
      {page === null ? <p role="alert">{w.invoiceGone}</p> : null}
      {page ? (
        <>
          <h1>{w.invoiceTitle(page.number, page.business)}</h1>
          <p>{w.invoiceFor(page.customer, page.date)}</p>
          {page.pdf ? (
            <p>
              <button type="button" className="btn btn-go" onClick={() => void download()}>
                {w.quotationPdf}
              </button>
            </p>
          ) : (
            <p role="status">{w.invoicePrinting}</p>
          )}
        </>
      ) : null}
      {problem ? <p role="alert">{problem}</p> : null}
    </main>
  );
}
